import { useCallback, useEffect, useState } from 'react';

/**
 * State backed by localStorage and SHARED across every consumer of the same
 * key. Two components reading `'viewer.selected'` see the same value and
 * re-render together when either of them writes — without this, the
 * ViewerToggle in the list pane could update its local copy while the sidebar
 * kept counting against a stale viewer (which is why "Zork's tasks" was
 * counting Adam's too).
 */

const VALUES = new Map<string, unknown>();
const LISTENERS = new Map<string, Set<() => void>>();

function readInto<T>(key: string, initial: T): T {
  if (VALUES.has(key)) return VALUES.get(key) as T;
  let v: T;
  try {
    const stored = localStorage.getItem(key);
    v = stored == null ? initial : (JSON.parse(stored) as T);
  } catch {
    v = initial;
  }
  VALUES.set(key, v);
  return v;
}

export function usePersistedState<T>(
  key: string,
  initial: T
): [T, (v: T | ((prev: T) => T)) => void] {
  const [, force] = useState(0);
  const value = readInto(key, initial);

  useEffect(() => {
    let set = LISTENERS.get(key);
    if (!set) {
      set = new Set();
      LISTENERS.set(key, set);
    }
    const fn = (): void => force((n) => n + 1);
    set.add(fn);
    return () => {
      set?.delete(fn);
    };
  }, [key]);

  const setAndPersist = useCallback(
    (v: T | ((prev: T) => T)) => {
      const prev = VALUES.get(key) as T;
      const next =
        typeof v === 'function' ? (v as (p: T) => T)(prev) : v;
      VALUES.set(key, next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* quota; ignore */
      }
      const set = LISTENERS.get(key);
      if (set) for (const fn of set) fn();
    },
    [key]
  );

  return [value, setAndPersist];
}
